/**
 * RegistrationSuccess Component
 *
 * Success state component displayed after profile completion.
 * Shows information about approval process, next steps, and email confirmation status.
 * Fetches and displays actual provider profile data from the API.
 *
 * @module components/provider-registration/RegistrationSuccess
 */

import React, { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import { Card } from '@/components/ui/Card';
import { useTheme } from '@/hooks/useTheme';
import { apiService } from '@/services/ApiService';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * Provider data for success display
 */
export interface ProviderSuccessData {
  firstName: string;
  lastName: string;
  email: string;
  practiceName: string;
  role: string;
  providerNumber: string;
  practiceAddress: string;
  bio?: string;
}

/**
 * API loading state
 */
interface LoadingState {
  isLoading: boolean;
  error: string | null;
  data: ProviderSuccessData | null;
}

/**
 * RegistrationSuccess component props interface
 */
export interface RegistrationSuccessProps {
  /** Custom class name */
  className?: string;
}

/**
 * RegistrationSuccess component for displaying successful registration
 * Fetches and displays actual provider profile data from the API
 *
 * @example
 * ```tsx
 * <RegistrationSuccess />
 * ```
 */
export const RegistrationSuccess: React.FC<RegistrationSuccessProps> = ({
  className
}) => {
  const logger = getLogger();
  const { isDark } = useTheme();
  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: true,
    error: null,
    data: null
  });

  /**
   * Fetch provider profile data from API
   */
  const fetchProviderProfile = async () => {
    try {
      setLoadingState(prev => ({ ...prev, isLoading: true, error: null }));

      // Fetch provider profile data
      const profileResponse = await apiService.provider.getProfile();

      // Transform API response to component data structure
      const providerData: ProviderSuccessData = {
        firstName: profileResponse.first_name || '',
        lastName: profileResponse.last_name || '',
        email: profileResponse.email || '',
        practiceName: profileResponse.practice_name || '',
        practiceAddress: profileResponse.practice_address || '',
        role: profileResponse.provider_role || '',
        providerNumber: profileResponse.ahpra_number || '',
        bio: profileResponse.bio || ''
      };

      setLoadingState({
        isLoading: false,
        error: null,
        data: providerData
      });

    } catch (error) {
      logger.error('Failed to fetch provider profile:', error);
      setLoadingState({
        isLoading: false,
        error: 'Failed to load provider information. Please try refreshing the page.',
        data: null
      });
    }
  };

  // Fetch profile data on component mount
  useEffect(() => {
    fetchProviderProfile();
  }, []);

  // Show loading state
  if (loadingState.isLoading) {
    return (
      <div className={cn('w-full', className)}>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className={cn(
            "text-sm",
            isDark ? "text-secondary-400" : "text-secondary-600"
          )}>
            Loading your profile information...
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (loadingState.error) {
    return (
      <div className={cn('w-full', className)}>
        <Card className="mb-6">
          <div className="p-6 text-center">
            <div className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4",
              isDark ? "bg-red-900/20 text-red-400" : "bg-red-50 text-red-600"
            )}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className={cn(
              "text-xl font-semibold mb-2",
              isDark ? "text-red-400" : "text-red-600"
            )}>
              Unable to Load Profile
            </h2>
            <p className={cn(
              "text-sm mb-4",
              isDark ? "text-secondary-400" : "text-secondary-600"
            )}>
              {loadingState.error}
            </p>
            <button
              onClick={fetchProviderProfile}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                isDark
                  ? "bg-primary-600 hover:bg-primary-700 text-white"
                  : "bg-primary-600 hover:bg-primary-700 text-white"
              )}
            >
              Try Again
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // Show success state with fetched data
  const providerData = loadingState.data!;
  return (
    <div className={cn('w-full', className)}>
      {/* Success Header */}
      <div className="text-center mb-8">
        <h1 className={cn(
          "text-3xl font-bold mb-2",
          isDark ? "text-secondary-100" : "text-secondary-900"
        )}>
          Registration Successful!
        </h1>
        <p className={cn(
          "text-lg",
          isDark ? "text-secondary-400" : "text-secondary-600"
        )}>
          Welcome to the BiteWise provider network, {providerData.role === 'nurse' ? '': 'Dr. '}{providerData.firstName} {providerData.lastName}
        </p>
      </div>

      {/* Registration Summary Card */}
      <Card className="mb-6">
        <div className="p-6">
          <h2 className={cn(
            "text-xl font-semibold mb-4",
            isDark ? "text-secondary-100" : "text-secondary-900"
          )}>
            Registration Summary
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt className={cn(
                "text-sm font-medium",
                isDark ? "text-secondary-400" : "text-secondary-600"
              )}>
                Provider Name
              </dt>
              <dd className={cn(
                "text-base",
                isDark ? "text-secondary-100" : "text-secondary-900"
              )}>
                {providerData.firstName} {providerData.lastName}
              </dd>
            </div>

            <div>
              <dt className={cn(
                "text-sm font-medium",
                isDark ? "text-secondary-400" : "text-secondary-600"
              )}>
                Email Address
              </dt>
              <dd className={cn(
                "text-base",
                isDark ? "text-secondary-100" : "text-secondary-900"
              )}>
                {providerData.email}
              </dd>
            </div>

            <div>
              <dt className={cn(
                "text-sm font-medium",
                isDark ? "text-secondary-400" : "text-secondary-600"
              )}>
                Practice Name
              </dt>
              <dd className={cn(
                "text-base",
                isDark ? "text-secondary-100" : "text-secondary-900"
              )}>
                {providerData.practiceName}
              </dd>
            </div>

            <div>
              <dt className={cn(
                "text-sm font-medium",
                isDark ? "text-secondary-400" : "text-secondary-600"
              )}>
                Practice Address
              </dt>
              <dd className={cn(
                "text-base",
                isDark ? "text-secondary-100" : "text-secondary-900"
              )}>
                {providerData.practiceAddress}
              </dd>
            </div>

            <div>
              <dt className={cn(
                "text-sm font-medium",
                isDark ? "text-secondary-400" : "text-secondary-600"
              )}>
                Provider Number
              </dt>
              <dd className={cn(
                "text-base",
                isDark ? "text-secondary-100" : "text-secondary-900"
              )}>
                {providerData.providerNumber}
              </dd>
            </div>

            {/* Bio Field - only show if bio exists */}
            {providerData.bio && (
              <div className="md:col-span-2">
                <dt className={cn(
                  "text-sm font-medium",
                  isDark ? "text-secondary-400" : "text-secondary-600"
                )}>
                  Bio
                </dt>
                <dd className={cn(
                  "text-base mt-1",
                  isDark ? "text-secondary-100" : "text-secondary-900"
                )}>
                  {providerData.bio}
                </dd>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Next Steps Card */}
      <Card className="mb-6">
        <div className="p-6">
          <h2 className={cn(
            "text-xl font-semibold mb-4",
            isDark ? "text-secondary-100" : "text-secondary-900"
          )}>
            What Happens Next?
          </h2>

          <div className="space-y-4">
            {/* Step 1 */}
            <div className="flex items-start">
              <div className={cn(
                "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3",
                isDark ? "bg-primary-900/20" : "bg-primary-100"
              )}>
                <span className={cn(
                  "text-sm font-semibold",
                  isDark ? "text-primary-400" : "text-primary-600"
                )}>1</span>
              </div>
              <div>
                <h3 className={cn(
                  "font-medium",
                  isDark ? "text-secondary-100" : "text-secondary-900"
                )}>
                  Administrative Review
                </h3>
                <p className={cn(
                  "text-sm",
                  isDark ? "text-secondary-400" : "text-secondary-600"
                )}>
                  Our team will verify your credentials and provider information within 1 business day.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start">
              <div className={cn(
                "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3",
                isDark ? "bg-primary-900/20" : "bg-primary-100"
              )}>
                <span className={cn(
                  "text-sm font-semibold",
                  isDark ? "text-primary-400" : "text-primary-600"
                )}>2</span>
              </div>
              <div>
                <h3 className={cn(
                  "font-medium",
                  isDark ? "text-secondary-100" : "text-secondary-900"
                )}>
                  Email Confirmation
                </h3>
                <p className={cn(
                  "text-sm",
                  isDark ? "text-secondary-400" : "text-secondary-600"
                )}>
                  You'll receive an email notification once your account has been approved.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start">
              <div className={cn(
                "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3",
                isDark ? "bg-primary-900/20" : "bg-primary-100"
              )}>
                <span className={cn(
                  "text-sm font-semibold",
                  isDark ? "text-primary-400" : "text-primary-600"
                )}>3</span>
              </div>
              <div>
                <h3 className={cn(
                  "font-medium",
                  isDark ? "text-secondary-100" : "text-secondary-900"
                )}>
                  Start Referring Patients
                </h3>
                <p className={cn(
                  "text-sm",
                  isDark ? "text-secondary-400" : "text-secondary-600"
                )}>
                  Access your provider dashboard and begin referring patients to our network of Accredited Practicing Dietitians.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

    </div>
  );
};

export default RegistrationSuccess;